// src/components/charts/inflowScatterChart/InflowScatterChart.jsx
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import { format } from "date-fns";
import React, { useRef } from "react";
import { Box } from "@material-ui/core";
import { parseLocalDate } from "../../../utils";

ChartJS.register(LinearScale, PointElement, TimeScale, Tooltip, Legend);

const COLORS = [
  "#0072B2", // Blue
  "#009E73", // Teal green
  "#D55E00", // Vermillion
  "#CC79A7", // Reddish purple
  "#F0E442", // Yellow
  "#56B4E9", // Sky blue
];

const annotatedLinePlugin = {
  id: "annotatedVerticalLine",
  afterDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.();
    if (!active?.length) return;
    const x = active[0].element.x;
    const yScale = Object.values(chart.scales).find((s) => s.axis === "y");
    if (!yScale) return;
    const ctx = chart.ctx;

    ctx.save();
    // thick translucent stroke
    ctx.beginPath();
    ctx.moveTo(x, yScale.top);
    ctx.lineTo(x, yScale.bottom);
    ctx.lineWidth = 9;
    ctx.strokeStyle = "rgba(0, 0, 255, 0.2)";
    ctx.stroke();

    // thin darker stroke
    ctx.beginPath();
    ctx.moveTo(x, yScale.top);
    ctx.lineTo(x, yScale.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(0, 0, 255, 0.4)";
    ctx.stroke();
    ctx.restore();
  },
};

export default function InflowScatterChart({ series, unit, height }) {
  const chartRef = useRef();

  // assign a single color per location
  const locationColors = {};
  let idx = 0;
  for (let s of series) {
    if (!locationColors[s.location]) {
      locationColors[s.location] = COLORS[idx % COLORS.length];
      idx++;
    }
  }

  const datasets = series.map((s) => {
    const color = locationColors[s.location];
    const isDissolved = s.parameter.toLowerCase().includes("diss");
    return {
      label: s.label,
      data: s.data,
      pointRadius: 5,
      pointHoverRadius: 6,
      backgroundColor: isDissolved ? "#fff" : color,
      borderColor: color,
      borderWidth: isDissolved ? 2 : 0,
    };
  });

  return (
    <Box height={height} width="100%">
      <Scatter
        ref={chartRef}
        data={{ datasets }}
        options={{
          maintainAspectRatio: false,
          interaction: { mode: "nearest", axis: "x", intersect: false },
          plugins: {
            tooltip: {
              callbacks: {
                title: (items) =>
                  format(parseLocalDate(items[0].raw.date), "MMM dd, yyyy"),
                label: (ti) => {
                  const { y, location_name, parameter } = ti.raw;
                  return `${y.toFixed(
                    1
                  )} ${unit} | ${location_name}, ${parameter}`;
                },
              },
              backgroundColor: "#fff",
              titleColor: "#333",
              bodyColor: "#333",
              borderColor: "#999",
              borderWidth: 1,
              cornerRadius: 6,
              padding: 10,
            },
            legend: { position: "top" },
          },
          scales: {
            x: {
              type: "time",
              time: { unit: "month", displayFormats: { month: "MM/YY" } },
            },
            y: {
              title: { display: true, text: `Concentration (${unit})` },
            },
          },
        }}
        plugins={[annotatedLinePlugin]}
      />
    </Box>
  );
}
